
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PartnerComparison = () => {
  const [activeTab, setActiveTab] = useState('summary');

  const comparisonData = {
    summary: [
      { feature: "Start-up Cost", juv: "$0 out of pocket", own: "$100K–$250K+ investment" },
      { feature: "Time to Launch", juv: "Under 30 days", own: "6–12 months" },
      { feature: "Staffing & HR", juv: "We handle it all", own: "You manage staff" },
      { feature: "Revenue Model", juv: "Shared + scalable", own: "100% profit, 100% risk" },
      { feature: "Marketing & Bookings", juv: "Done-for-you ads & app", own: "You build funnel & campaign" }
    ],
    cost: [
      { feature: "Initial Investment", juv: "$0", own: "$100K–$250K" },
      { feature: "Monthly Operating Cost", juv: "$0 (we cover all)", own: "$15K–25K/month" },
      { feature: "Staff Salaries", juv: "Included", own: "$8K–15K/month" },
      { feature: "Equipment & Supplies", juv: "Included", own: "$50K–100K upfront" },
      { feature: "Marketing Budget", juv: "Included", own: "$3K–8K/month" }
    ],
    full: [
      { feature: "Start-up Cost", juv: "$0 out of pocket", own: "$100K–$250K+ investment" },
      { feature: "Time to Launch", juv: "Under 30 days", own: "6–12 months" },
      { feature: "Staffing & HR", juv: "We handle it all", own: "You manage staff" },
      { feature: "Licensing & Compliance", juv: "Fully managed by JUV", own: "Requires legal setup and oversight" },
      { feature: "Treatment Menu", juv: "Proven, high-demand, curated services", own: "You build and test from scratch" },
      { feature: "Revenue Model", juv: "Shared + scalable", own: "100% profit, 100% risk" },
      { feature: "Marketing & Bookings", juv: "Done-for-you ads & app", own: "You build funnel & campaign" },
      { feature: "Membership System", juv: "Already built", own: "Requires tech + compliance" },
      { feature: "Branding", juv: "You keep your identity", own: "Requires integration" }
    ]
  };

  const tabs = [
    { id: 'summary', label: 'Summary', badge: 'Most Popular' },
    { id: 'cost', label: 'Cost & Time', badge: 'Most dental owners choose JUV after seeing this' },
    { id: 'full', label: 'Full Breakdown', badge: '' }
  ];

  return (
    <section className="py-32 bg-juv-cream-white">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-serif font-light text-juv-deep-chestnut mb-8">
            JUV Partnership vs. Building Your Own Medspa
          </h2>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-col md:flex-row justify-center gap-4 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 rounded-2xl font-medium transition-all duration-300 relative ${
                activeTab === tab.id
                  ? 'bg-juv-terracotta text-white shadow-lg scale-105'
                  : 'bg-white text-juv-deep-chestnut hover:bg-juv-soft-blush/20'
              }`}
            >
              {tab.label}
              {tab.badge && activeTab !== tab.id && (
                <span className="absolute -top-2 -right-2 bg-juv-rose-clay text-white text-xs px-2 py-1 rounded-full">
                  {tab.badge === 'Most Popular' ? 'Most Popular' : ''}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'cost' && (
          <div className="text-center mb-8">
            <p className="text-juv-terracotta font-medium bg-juv-rose-clay/10 px-4 py-2 rounded-full inline-block">
              Most dental owners choose JUV after seeing the Cost Breakdown.
            </p>
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden md:block">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl shadow-luxury overflow-hidden"
          >
            <div className="grid grid-cols-3 bg-juv-deep-chestnut text-white p-6">
              <div className="font-semibold text-lg">Feature / Factor</div>
              <div className="font-semibold text-lg text-center">JUV Plug-In Model</div>
              <div className="font-semibold text-lg text-center">Build Your Own Medspa</div>
            </div>
            
            {comparisonData[activeTab].map((row, index) => (
              <div key={index} className={`grid grid-cols-3 p-6 border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="font-medium text-juv-deep-chestnut">{row.feature}</div>
                <div className="text-green-600 font-medium text-center">{row.juv}</div>
                <div className="text-red-600 text-center">{row.own}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-6">
          {comparisonData[activeTab].map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <h3 className="font-semibold text-juv-deep-chestnut mb-4 text-lg">{row.feature}</h3>
              <div className="space-y-3">
                <div className="p-3 bg-green-50 rounded-xl">
                  <div className="text-sm text-green-600 font-medium mb-1">JUV Plug-In:</div>
                  <div className="text-green-700 font-semibold">{row.juv}</div>
                </div>
                <div className="p-3 bg-red-50 rounded-xl">
                  <div className="text-sm text-red-600 font-medium mb-1">Build Your Own:</div>
                  <div className="text-red-700">{row.own}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-lg text-juv-deep-chestnut/70 mb-6">
            Still thinking about building it yourself?<br />
            Try JUV first—risk-free. We'll prove it works in 30 days or less.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerComparison;
